import type { BillingCycle, CurrencyCode } from './subscription.models';
import {
  resolvePlanLimitFieldForBilling,
  type SubscriptionAddOnLineRequest,
} from './billing-operations.models';

export type WorkspaceSubscriptionStatus =
  | 'TRIALING'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'SUSPENDED'
  | 'CANCELED'
  | 'FREE';

export type WorkspaceBillingCycleApi = 'monthly' | 'annual';

export type WorkspaceSubscriptionPlanApi = {
  id: string;
  planCode: string;
  planName: string;
  description: string | null;
  aiCreditsPerMonth: string;
  maxUsers: string;
  maxContacts: string;
  maxCompanies: string;
  maxDeals: string;
  maxLeads: string;
  maxForms: string;
  maxWorkflows: string;
  maxCustomFields: string;
  maxEmailIntegrationAccounts: string;
  maxApiKeys: string;
  maxWebhooks: string;
  storageLimit: string;
  maxTeamMembers: string;
  maxCampaigns: string;
  maxWorkspaces: string;
  maxPipelines: string;
  retentionPeriodDays: string;
  annualLimits?: Record<string, string> | null;
  monthlyPriceInr: number;
  annualPriceInr: number;
  monthlyPriceUsd: number;
  annualPriceUsd: number;
  freeTrialDays: number;
};

export type WorkspaceSubscriptionPromoApi = {
  promoCode: string | null;
  promoName: string | null;
  discountType: string | null;
  discountValue: number | null;
  discountAmount: number | null;
};

export type WorkspaceSubscriptionPricingApi = {
  currency: string;
  subtotal: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  dueToday: number;
};

export type WorkspaceSubscriptionApiResponse = {
  id: string | null;
  workspaceId: string;
  status: WorkspaceSubscriptionStatus;
  billingCycle: WorkspaceBillingCycleApi;
  seats: number;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  autopayAuthorizedAt?: string | null;
  gatewayPaymentMethod?: string | null;
  currency?: string;
  promo?: WorkspaceSubscriptionPromoApi;
  pricing?: WorkspaceSubscriptionPricingApi;
  plan: WorkspaceSubscriptionPlanApi;
  consumedTrialPlanIds?: string[];
  trialDowngradeNotice?: TrialDowngradeNoticeApi | null;
};

export type TrialDowngradeNoticeApi = {
  previousPlanName: string;
  previousPlanCode: string;
  previousAiCreditsPerMonth: string;
  endedAt: string;
};

export type CreateWorkspaceSubscriptionRequest = {
  planId: string;
  billingCycle: BillingCycle | 'annual';
  seats: number;
  promoCode?: string;
  currency?: CurrencyCode;
  addOns?: SubscriptionAddOnLineRequest[];
};

export type OverviewPlanDisplay = {
  name: string;
  description: string;
  users: string;
  price: string;
  status: string;
  nextRenewal: string;
  aiCreditsLabel: string;
  aiCredits: string;
  billingCycle: string;
};

function formatCurrencyAmount(amount: number, currency: CurrencyCode): string {
  const rounded = Math.round(amount);
  if (currency === 'INR') {
    return `₹${rounded.toLocaleString('en-IN')}`;
  }
  return `$${rounded.toLocaleString('en-US')}`;
}

function formatStatus(status: WorkspaceSubscriptionStatus): string {
  switch (status) {
    case 'TRIALING':
      return 'Trial';
    case 'ACTIVE':
      return 'Active';
    case 'FREE':
      return 'Free';
    case 'PAST_DUE':
      return 'Past due';
    case 'SUSPENDED':
      return 'Suspended';
    case 'CANCELED':
      return 'Canceled';
    default:
      return status;
  }
}

function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('en-GB');
}

function formatPlanName(planName: string): string {
  const trimmed = planName.trim();
  if (!trimmed) {
    return trimmed;
  }
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1).toLowerCase()} Plan`;
}

function perUserPrice(
  subscription: WorkspaceSubscriptionApiResponse,
  currency: CurrencyCode,
): number {
  const plan = subscription.plan;
  if (subscription.billingCycle === 'monthly') {
    return currency === 'INR' ? plan.monthlyPriceInr : plan.monthlyPriceUsd;
  }
  return currency === 'INR' ? plan.annualPriceInr : plan.annualPriceUsd;
}

export function mapSubscriptionToOverviewPlan(
  subscription: WorkspaceSubscriptionApiResponse,
  currency: CurrencyCode = 'INR',
): OverviewPlanDisplay {
  const perUser = perUserPrice(subscription, currency);
  const isMonthly = subscription.billingCycle === 'monthly';
  const cycleLabel = isMonthly ? 'Monthly' : 'Annual';
  const aiCredits = resolvePlanLimitFieldForBilling(
    subscription.plan,
    'aiCreditsPerMonth',
    subscription.billingCycle,
  ).trim();

  const renewalDate = isMonthly
    ? subscription.status === 'TRIALING' && subscription.trialEndsAt
      ? subscription.trialEndsAt
      : subscription.currentPeriodEnd
    : subscription.currentPeriodEnd;

  return {
    name: formatPlanName(subscription.plan.planName),
    description: subscription.plan.description?.trim() || '',
    users: `${subscription.seats} User${subscription.seats === 1 ? '' : 's'}`,
    price: `${formatCurrencyAmount(perUser, currency)}/${isMonthly ? 'Month' : 'Annum'}`,
    status: formatStatus(subscription.status),
    nextRenewal: formatDate(renewalDate),
    aiCreditsLabel: isMonthly ? 'AI Credits/Month' : 'AI Credits/Annum',
    aiCredits: aiCredits || '—',
    billingCycle: cycleLabel,
  };
}

export function toSubscribeBillingCycle(
  cycle: BillingCycle,
): CreateWorkspaceSubscriptionRequest['billingCycle'] {
  return cycle === 'yearly' ? 'yearly' : 'monthly';
}

export function hasActivePaidSubscription(
  subscription: WorkspaceSubscriptionApiResponse,
): boolean {
  if (subscription.plan.planCode.toLowerCase() === 'free') {
    return false;
  }

  return (
    subscription.status === 'TRIALING' ||
    subscription.status === 'ACTIVE' ||
    subscription.status === 'PAST_DUE' ||
    subscription.status === 'SUSPENDED'
  );
}

export function canWorkspacePurchaseAddOns(
  subscription: WorkspaceSubscriptionApiResponse | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }
  if (subscription.plan.planCode.toLowerCase() === 'free') {
    return false;
  }
  return subscription.status === 'ACTIVE';
}

export function isOnFreePlan(
  subscription: WorkspaceSubscriptionApiResponse | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }
  return (
    subscription.status === 'FREE' &&
    subscription.plan.planCode.toLowerCase() === 'free'
  );
}

export function isOnFreeTrial(
  subscription: WorkspaceSubscriptionApiResponse | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }
  return subscription.status === 'TRIALING';
}

export function formatPlanCreditsLabel(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '0';
  }
  const numeric = Number(trimmed.replace(/,/g, ''));
  if (Number.isFinite(numeric)) {
    return numeric.toLocaleString('en-IN');
  }
  return trimmed;
}
